import classNames from "classnames";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IOrganizations } from "../../../../setup/types/Organisation";
import {
  isPhoneNumberComplete,
  isPrefixDefinitivelyInvalid,
  SA_FALLBACK_CONFIG,
} from "../../../../setup/util/phoneRuleConfig.helpers";
import { ReactComponent as SellerIcon } from "../../../../assets/images/svg/twoPartyContract/party-seller.svg";
import { ReactComponent as BrokerIcon } from "../../../../assets/images/svg/twoPartyContract/party-broker.svg";
import FloatingField from "../ui/FloatingField";
import SaudiPhoneField from "../ui/SaudiPhoneField";
import { TwoPartyContractInputs } from "../schema";
import styles from "../styles.module.scss";

interface IPartyCardProps {
  icon: React.ReactNode;
  name: string;
  subtitle: string;
  role: string;
  highlighted?: boolean;
}

const PartyCard = ({
  icon,
  name,
  subtitle,
  role,
  highlighted,
}: IPartyCardProps) => (
  <div className={styles.partyCard}>
    <div className={styles.partyIdentity}>
      <span className={styles.partyIcon}>{icon}</span>
      <div className={styles.partyText}>
        <p className={styles.partyName}>{name}</p>
        <p className={styles.partySubtitle}>{subtitle}</p>
      </div>
    </div>
    <span
      className={classNames(styles.rolePill, {
        [styles.rolePillHighlighted]: highlighted,
      })}>
      {role}
    </span>
  </div>
);

interface IStepPartiesProps {
  organization?: IOrganizations;
}

/**
 * Step two: the signed-in organization as the seller and Waffy as the guarantor broker (both
 * fixed; Waffy's card is for display, it is not sent as a party), and the buyer the seller invites.
 */
const StepParties = ({ organization }: IStepPartiesProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext<TwoPartyContractInputs>();
  const isCompany = useWatch({ control, name: "buyer.isCompany" });

  return (
    <div className={styles.fieldsLg}>
      <div className={styles.partyCards}>
        <PartyCard
          icon={<SellerIcon />}
          name={organization?.name || ""}
          subtitle={t("lbl_two_party_seller_subtitle")}
          role={t("lbl_seller_role")}
        />
        <PartyCard
          icon={<BrokerIcon />}
          name={t("lbl_waffy")}
          subtitle={t("lbl_guarantor_broker")}
          role={t("lbl_broker_role")}
          highlighted
        />
      </div>

      <div className={styles.buyerBlock}>
        <div className={styles.buyerHeader}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>{t("lbl_buyer_details")}</h3>
            <p className={styles.buyerHint}>{t("lbl_buyer_details_hint")}</p>
          </div>
          <Controller
            name="buyer.isCompany"
            control={control}
            render={({ field }) => (
              <div className={styles.segmented} role="tablist">
                {[false, true].map((company) => (
                  <button
                    key={`${company}`}
                    type="button"
                    role="tab"
                    aria-selected={field.value === company}
                    className={classNames(styles.segment, {
                      [styles.segmentActive]: field.value === company,
                    })}
                    onClick={() => field.onChange(company)}>
                    {company ? t("lbl_company") : t("lbl_individual")}
                  </button>
                ))}
              </div>
            )}
          />
        </div>

        <div className={styles.buyerFields}>
          <Controller
            name="buyer.name"
            control={control}
            render={({ field }) => (
              <FloatingField
                {...field}
                small
                maxLength={100}
                label={t("lbl_name")}
                errorMessage={errors?.buyer?.name?.message?.toString()}
              />
            )}
          />
          <Controller
            name="buyer.phoneNational"
            control={control}
            render={({ field, fieldState }) => (
              <SaudiPhoneField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                label={t("lbl_mobile_number")}
                // Held back while the number is still being typed, unless its start can no
                // longer be a Saudi mobile.
                errorMessage={
                  fieldState.isTouched ||
                  isPhoneNumberComplete(field.value, SA_FALLBACK_CONFIG) ||
                  isPrefixDefinitivelyInvalid(field.value, SA_FALLBACK_CONFIG)
                    ? errors?.buyer?.phoneNational?.message?.toString()
                    : undefined
                }
              />
            )}
          />
          {isCompany && (
            <>
              <Controller
                name="buyer.companyName"
                control={control}
                render={({ field }) => (
                  <FloatingField
                    {...field}
                    maxLength={100}
                    label={t("lbl_company_name")}
                    errorMessage={errors?.buyer?.companyName?.message?.toString()}
                  />
                )}
              />
              <Controller
                name="buyer.companyUnifiedNumber"
                control={control}
                render={({ field }) => (
                  <FloatingField
                    {...field}
                    inputMode="numeric"
                    maxLength={10}
                    label={t("lbl_unified_number")}
                    hint={t("lbl_unified_number_hint")}
                    onChange={(event) =>
                      field.onChange(event.target.value.replace(/\D/g, ""))
                    }
                    errorMessage={errors?.buyer?.companyUnifiedNumber?.message?.toString()}
                  />
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepParties;
